// The support button in the bottom-right corner of the signed-in app.
//
// A short form: what kind of thing it is, and what happened. Everything else
// we would have had to ask for is sent with it – who they are comes from the
// session on the server (routes/support.js), and the page, the browser and a
// picture of the screen are gathered here. The picture is shown before it is
// sent and can be left out: it is whatever was on the screen, pupils' names
// included.
//
// Self-contained on purpose. It builds its own markup and reads nothing from
// app.js, so a report can still be sent when the thing being reported is the
// app having fallen over.
(function () {
    'use strict';

    const SHOT_SRC = '/vendor/modern-screenshot/index.js';
    // Wide enough to read small text in, small enough to email.
    const SHOT_MAX_WIDTH = 1600;
    const SHOT_MAX_CHARS = 6 * 1024 * 1024;
    // The library's own patience for one image: lazy pictures further down the
    // page never load, and it would otherwise wait 30 seconds for them.
    const SHOT_MEDIA_TIMEOUT_MS = 2500;
    const SHOT_TIMEOUT_MS = 12000;
    const SUPPORT_EMAIL = 'support@blueacorn.org';

    // The last few things that went wrong on the page, sent with the report:
    // usually the answer to "what happened" that the sender could not see.
    const recentErrors = [];
    const noteError = (text) => {
        recentErrors.push(`${new Date().toISOString().slice(11, 19)} ${String(text).slice(0, 280)}`);
        if (recentErrors.length > 5) recentErrors.shift();
    };
    window.addEventListener('error', (e) => {
        if (e.message) noteError(`${e.message} (${(e.filename || '').split('/').pop()}:${e.lineno || 0})`);
    });
    window.addEventListener('unhandledrejection', (e) => {
        noteError(`Unhandled: ${(e.reason && (e.reason.message || e.reason)) || 'rejection'}`);
    });

    const el = (tag, attrs = {}, children = []) => {
        const node = document.createElement(tag);
        for (const [key, value] of Object.entries(attrs)) {
            if (key === 'text') node.textContent = value;
            else if (key === 'html') node.innerHTML = value;
            else node.setAttribute(key, value);
        }
        for (const child of children) node.appendChild(child);
        return node;
    };

    // Lucide's circle-question-mark, as the rest of the app's icons are Lucide's.
    const ICON_HELP = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>';
    const ICON_CLOSE = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

    // ── Markup ────────────────────────────────────────────
    const kinds = [['problem', 'Something’s broken'], ['question', 'Question'], ['idea', 'Idea']];
    const kindRow = el('div', { class: 'sup-kinds', role: 'radiogroup', 'aria-label': 'What is this about?' },
        kinds.map(([value, label], i) => {
            const input = el('input', { type: 'radio', name: 'sup-kind', value });
            if (i === 0) input.checked = true;
            return el('label', { class: 'sup-kind' }, [input, el('span', { text: label })]);
        }));

    const message = el('textarea', {
        id: 'sup-message', rows: '4', maxlength: '5000', required: '',
        placeholder: 'What happened, and what did you expect?',
        'aria-label': 'Your message',
    });

    const shotToggle = el('input', { type: 'checkbox', id: 'sup-shot-toggle' });
    shotToggle.checked = true;
    const shotImg = el('img', { class: 'sup-shot-img', alt: 'The screenshot that will be sent', hidden: '' });
    const shotNote = el('span', { class: 'sup-shot-note', text: 'Taking a screenshot…' });
    const shotBox = el('div', { class: 'sup-shot' }, [shotImg, shotNote]);
    const shotRow = el('label', { class: 'sup-shot-row', for: 'sup-shot-toggle' }, [
        shotToggle, el('span', { text: 'Include a screenshot of this page' }),
    ]);

    const error = el('p', { class: 'sup-error', role: 'alert', hidden: '' });
    const replyNote = el('span', { class: 'sup-reply' });
    const sendBtn = el('button', { type: 'submit', class: 'btn btn-primary btn-sm', text: 'Send' });

    const form = el('form', { class: 'sup-form', novalidate: '' }, [
        kindRow, message, shotBox, shotRow, error,
        el('div', { class: 'sup-foot' }, [replyNote, sendBtn]),
    ]);

    const doneText = el('p', { class: 'sup-done-text' });
    const doneBtn = el('button', { type: 'button', class: 'btn btn-secondary btn-sm', text: 'Close' });
    const done = el('div', { class: 'sup-done', hidden: '' }, [
        el('h3', { text: 'Sent – thank you' }), doneText, doneBtn,
    ]);

    const closeBtn = el('button', { type: 'button', class: 'sup-close', 'aria-label': 'Close', html: ICON_CLOSE });
    const panel = el('div', {
        class: 'sup-panel t-dropdown', 'data-origin': 'bottom-right', id: 'sup-panel',
        role: 'dialog', 'aria-labelledby': 'sup-title', hidden: '',
    }, [
        el('div', { class: 'sup-head' }, [el('h2', { id: 'sup-title', text: 'How can we help?' }), closeBtn]),
        form, done,
    ]);

    const launcher = el('button', {
        type: 'button', class: 'sup-launcher',
        'aria-expanded': 'false', 'aria-controls': 'sup-panel', html: ICON_HELP,
    });
    launcher.appendChild(el('span', { text: 'Need Help?' }));

    const root = el('div', { class: 'sup-widget', 'data-support-widget': '' }, [panel, launcher]);
    document.body.appendChild(root);

    // ── Screenshot ────────────────────────────────────────
    let shotLib = null;
    function loadShotLib() {
        if (!shotLib) {
            shotLib = new Promise((resolve, reject) => {
                if (window.modernScreenshot) return resolve(window.modernScreenshot);
                const s = document.createElement('script');
                s.src = SHOT_SRC;
                s.onload = () => (window.modernScreenshot ? resolve(window.modernScreenshot) : reject(new Error('no library')));
                s.onerror = () => { shotLib = null; reject(new Error('library failed to load')); };
                document.head.appendChild(s);
            });
        }
        return shotLib;
    }

    // What is in the window now – not the whole scrolled page, which on a long
    // list is mostly things the sender was not looking at.
    async function takeScreenshot() {
        const lib = await loadShotLib();
        const width = window.innerWidth;
        const height = window.innerHeight;
        const capture = lib.domToJpeg(document.documentElement, {
            width, height,
            scale: Math.min(window.devicePixelRatio || 1, SHOT_MAX_WIDTH / width),
            quality: 0.8,
            backgroundColor: getComputedStyle(document.body).backgroundColor || '#f5efe7',
            // Not this widget, and nothing that is not being shown: most of
            // the app's markup is views that are hidden at any one time.
            filter: (node) => !(node instanceof Element) || !document.body.contains(node)
                || (!node.hasAttribute('data-support-widget') && getComputedStyle(node).display !== 'none'),
            style: { marginTop: `${-window.scrollY}px`, marginLeft: `${-window.scrollX}px` },
            timeout: SHOT_MEDIA_TIMEOUT_MS,
        });
        const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timed out')), SHOT_TIMEOUT_MS));
        const dataUrl = await Promise.race([capture, timeout]);
        if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/jpeg') || dataUrl.length > SHOT_MAX_CHARS) {
            throw new Error('unusable screenshot');
        }
        return dataUrl;
    }

    let shot = null;       // data URL, once taken
    let shotRun = 0;       // so a slow capture from an earlier opening is ignored
    function refreshShot() {
        const run = ++shotRun;
        shot = null;
        shotImg.hidden = true;
        shotNote.hidden = false;
        shotNote.textContent = 'Taking a screenshot…';
        shotToggle.disabled = false;
        syncShot();
        takeScreenshot().then((dataUrl) => {
            if (run !== shotRun) return;
            shot = dataUrl;
            shotImg.src = dataUrl;
            shotImg.hidden = false;
            shotNote.hidden = true;
        }).catch((err) => {
            if (run !== shotRun) return;
            noteError(`Screenshot failed: ${err && err.message}`);
            shotNote.textContent = 'Couldn’t take a screenshot – your message will still send.';
            shotToggle.checked = false;
            shotToggle.disabled = true;
        });
    }
    function syncShot() { shotBox.hidden = !shotToggle.checked && !shotToggle.disabled; }
    shotToggle.addEventListener('change', syncShot);

    // ── Open / close ──────────────────────────────────────
    let closeTimer = null;
    const isOpen = () => launcher.getAttribute('aria-expanded') === 'true';

    function open() {
        if (isOpen()) return;
        clearTimeout(closeTimer);
        const email = (document.getElementById('nav-user-email')?.textContent || '').trim();
        replyNote.textContent = email ? `We’ll reply to ${email}` : 'We’ll reply by email';
        setError('');
        form.hidden = false;
        done.hidden = true;
        // Before the panel shows, though the capture leaves the widget out
        // either way.
        refreshShot();
        panel.hidden = false;
        panel.classList.remove('is-closing');
        void panel.offsetWidth;
        panel.classList.add('is-open');
        launcher.setAttribute('aria-expanded', 'true');
        message.focus();
    }

    function close({ returnFocus = true } = {}) {
        if (!isOpen()) return;
        shotRun++;
        panel.classList.remove('is-open');
        panel.classList.add('is-closing');
        launcher.setAttribute('aria-expanded', 'false');
        closeTimer = setTimeout(() => { panel.hidden = true; panel.classList.remove('is-closing'); }, 200);
        if (returnFocus) launcher.focus();
    }

    launcher.addEventListener('click', () => (isOpen() ? close() : open()));
    closeBtn.addEventListener('click', () => close());
    doneBtn.addEventListener('click', () => close());
    root.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isOpen()) { e.stopPropagation(); close(); }
    });

    // ── Send ──────────────────────────────────────────────
    function setError(text) {
        error.textContent = text;
        error.hidden = !text;
    }

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const text = message.value.trim();
        if (!text) { setError('Please tell us what happened.'); message.focus(); return; }
        setError('');
        sendBtn.disabled = true;
        sendBtn.textContent = 'Sending…';
        try {
            const res = await fetch('/api/support/report', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: text,
                    category: form.querySelector('input[name="sup-kind"]:checked')?.value || 'problem',
                    screenshot: shotToggle.checked ? shot : null,
                    context: {
                        url: location.href,
                        title: document.title,
                        viewport: `${window.innerWidth}×${window.innerHeight}`,
                        screen: `${screen.width}×${screen.height} @${window.devicePixelRatio || 1}x`,
                        language: navigator.language,
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        errors: recentErrors,
                    },
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.status === 401) throw new Error(`Please sign in again, or email ${SUPPORT_EMAIL}.`);
            if (!res.ok) throw new Error(data.error || `We couldn’t send that. Please email ${SUPPORT_EMAIL} instead.`);
            message.value = '';
            doneText.textContent = data.replyTo ? `We’ll reply to ${data.replyTo}.` : 'We’ll reply by email.';
            form.hidden = true;
            done.hidden = false;
            doneBtn.focus();
        } catch (err) {
            // fetch's own failure reads "Failed to fetch", which helps nobody.
            setError(err instanceof TypeError ? `Network error – not sent. Check your connection, or email ${SUPPORT_EMAIL}.` : err.message);
        } finally {
            sendBtn.disabled = false;
            sendBtn.textContent = 'Send';
        }
    });

    // ── Keeping clear of the progress dock ────────────────
    // #pack-dock takes the same corner while companions generate. The button
    // rides above it rather than under it, however tall the dock has grown.
    const dock = document.getElementById('pack-dock');
    if (dock && window.ResizeObserver) {
        const lift = () => {
            const h = dock.hidden ? 0 : dock.offsetHeight;
            root.style.setProperty('--sup-lift', h ? `${h + 12}px` : '0px');
        };
        new ResizeObserver(lift).observe(dock);
        new MutationObserver(lift).observe(dock, { attributes: true, attributeFilter: ['hidden'] });
        lift();
    }
})();
